import { Widget } from '@sprinklr/stories/widget/Widget';
import { Theme } from 'models/Theme/Theme';
import { CollapseProps, TunablesCollapse } from 'components/Sidebar/RenderTunables/RenderTunables';
import i18n from 'i18next';
import {
    headerTunables,
    opacityTunables,
    overflowTunables,
} from 'components/Sidebar/RenderTunables/SharedTunables';
import {
    xAxisTunables,
    yAxisTunables,
    gridTunables,
    legendTunables,
    metaInfoTunables,
} from 'components/Sidebar/RenderTunables/HyperspaceSharedTunables';
import React from 'react';
import ActionCheckboxGroup from 'components/_UI/Forms/Actions/ActionCheckboxGroup';
import LabelGroup from 'components/_UI/Forms/LabelGroup/LabelGroup';
import ActionInput from 'components/_UI/Forms/Actions/ActionInput';
import ActionSliderGroup from 'components/_UI/Forms/Actions/ActionSliderGroup';
import ActionCheckboxSliderGroup from 'components/_UI/Forms/Actions/ActionCheckboxSliderGroup';
import { trendingPostTunables } from '../withTrendingPost/tunables';

export const HyperSpaceLineChartWidgetTunables = (
    widget: Widget,
    mergedTheme: Theme
): TunablesCollapse[] => [
    {
        props: {
            label: i18n.t('Line Chart'),
            startOpen: true,
        } as CollapseProps,
        children: [
            ...headerTunables(widget),
            opacityTunables(widget),
            overflowTunables(widget),
            <ActionCheckboxGroup
                data-testid='checkbox-zero-fill-tunables'
                key='widget.options.zeroFill'
                label={i18n.t('Zero Fill')}
                option={widget.options}
                value='zeroFill'
            />,
            gridTunables(widget),
            <ActionSliderGroup
                data-testid='slider-line-width-tunables'
                key='widget.options.lineWidth'
                label={i18n.t('Line Width')}
                option={widget.options.line}
                value='strokeWidth'
                showValue
                min={1}
                max={10}
            />,
            <ActionCheckboxSliderGroup
                data-testid='options-point-markers-tunables'
                key='pointMarkers'
                label={i18n.t('Point Markers')}
                showValue
                checkbox={{
                    option: widget.options.pointMarkers,
                    value: 'enabled',
                }}
                slider={{
                    option: widget.options.pointMarkers,
                    value: 'size',
                    min: 0,
                    max: 50,
                }}
            />,
        ],
    },
    metaInfoTunables({ widget, color: true, image: false }),
    xAxisTunables({ widget, mergedTheme }),
    yAxisTunables({ widget, mergedTheme, axisIsDim: false }),
    {
        props: {
            label: i18n.t('Spikes'),
            startOpen: true,
            isEnabled: widget.options.spikes.enabled,
            option: widget.options.spikes,
            value: 'enabled',
            ['data-testid']: 'spikes-tunable',
        } as CollapseProps,
        children: [
            <ActionCheckboxSliderGroup
                data-testid='options-spikes-marker-tunables'
                key='pointMarkersSize'
                label={i18n.t('Spike Marker')}
                showValue
                checkbox={{
                    option: widget.options.spikes.marker,
                    value: 'enabled',
                }}
                slider={{
                    option: widget.options.spikes.marker,
                    value: 'size',
                    min: 0,
                    max: 50,
                }}
            />,
            widget.options.spikes.marker ? (
                <ActionCheckboxGroup
                    key='spikes.marker.animation'
                    label={i18n.t('Animation')}
                    option={widget.options.spikes.marker.animation}
                    value='enabled'
                    data-testid='options-spikes-marker-animation-enabled'
                />
            ) : null,
            <ActionCheckboxSliderGroup
                data-testid='options-spikes-metric-value-tunables'
                key='metric'
                label={i18n.t('Metric Value')}
                showValue
                checkbox={{
                    option: widget.options.spikes.value,
                    value: 'enabled',
                }}
                slider={{
                    option: widget.options.spikes.value,
                    value: 'size',
                    min: 0,
                    max: 50,
                }}
            />,
            widget.options?.spikes?.value?.enabled
                ? [
                      <ActionCheckboxGroup
                          key='metric.format'
                          label={i18n.t('Shortened Number')}
                          option={widget.options.spikes.value.formats.numberFormat}
                          value='useShortened'
                          data-testid='options-metric-number-format'
                      />,

                      <LabelGroup key='metric.format.decimals' label={i18n.t('Decimals')}>
                          <ActionInput
                              min={0}
                              max={10}
                              type='number'
                              option={widget.options.spikes.value.formats.numberFormat}
                              value='decimals'
                              data-testid='options-metric-decimals'
                          />
                      </LabelGroup>,
                  ]
                : null,
        ],
    },
    legendTunables(widget),
    trendingPostTunables({ widget }),
];
